import React from 'react';
import { Box, Typography } from '@mui/material';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { ExecutiveSummaryResponse, WorkloadSimulatorResponse } from '../../services/benchmarkService';
import { MetricInfo, NotComputed, PanelError, PanelSkeleton, SectionCard } from './BenchmarkPrimitives';
import { missedCount, outcomeAt } from './benchmarkModel';
import { DEFINITIONS, count, fteFromAlerts, hasValue, percent } from './BenchmarkVocabulary';
import { dsVar, ink } from '../../theme/tokens';

/**
 * The two halves of the decision, side by side and named as such.
 *
 * Reducing the caseload is a cost question and keeping the risk is a compliance
 * question, and they pull against each other — so they are two labelled
 * columns rather than five undifferentiated metric cards, which is what the
 * page used to open with.
 *
 * Both are computed at the review level the reader currently has selected, so
 * moving the slider moves these. They were previously fixed to the API's
 * "suppress the two lowest tiers" definition, which silently described a
 * different policy from the one the recommendation named.
 */

const Stat: React.FC<{
    label: string;
    value: string;
    detail?: string;
    accent: string;
    definition?: string;
    /** Too few cases behind it to state as a result: muted and labelled. */
    indicative?: boolean;
}> = ({ label, value, detail, accent, definition, indicative = false }) => (
    <Box>
        <Box className="flex items-center gap-1.5">
            <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), fontWeight: 500 }}>{label}</Typography>
            {definition && <MetricInfo definition={definition} />}
        </Box>
        <Typography sx={{ fontSize: indicative ? '1.375rem' : '1.75rem', fontWeight: indicative ? 600 : 800, color: indicative ? dsVar('fg-muted') : accent, lineHeight: 1.2, mt: 0.25 }}>
            {value}
        </Typography>
        {indicative && (
            <Typography sx={{ fontSize: '0.6875rem', color: ink('#FBBF24'), mt: 0.25 }}>Indicative only</Typography>
        )}
        {detail && (
            <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), mt: 0.25, lineHeight: 1.5 }}>
                {detail}
            </Typography>
        )}
    </Box>
);

interface SavingsAndCoverageProps {
    summary: ExecutiveSummaryResponse | null;
    simulator: WorkloadSimulatorResponse | null;
    /** The review level currently selected, as a fraction of the queue. */
    reviewFraction: number;
    /** The institution's average handling time, owned by the tab. */
    minutesPerAlert: number;
    /** Too few cases to size a change: figures muted, staffing not estimated. */
    indicative?: boolean;
    loading?: boolean;
    error?: string | null;
    onRetry?: () => void;
}

export const SavingsAndCoverage: React.FC<SavingsAndCoverageProps> = ({
    summary, simulator, reviewFraction, minutesPerAlert, indicative = false, loading, error, onRetry
}) => {
    if (error) return <PanelError message={error} onRetry={onRetry} />;
    if (loading) {
        return (
            <Box className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <PanelSkeleton height={230} />
                <PanelSkeleton height={230} />
            </Box>
        );
    }
    if (!summary || !simulator) return null;

    const outcome = outcomeAt(simulator, reviewFraction);
    const alertsReviewed = Math.round(simulator.totalAlertCount * reviewFraction);
    const alertsAvoided = simulator.totalAlertCount - alertsReviewed;

    const fte = fteFromAlerts(alertsAvoided, minutesPerAlert);
    const hours = minutesPerAlert > 0 ? (alertsAvoided * minutesPerAlert) / 60 : null;

    const confirmedMissed = missedCount(summary.totals.truePositives, outcome.truePositiveRecall);
    const sarsMissed = missedCount(summary.totals.sarsFiled, outcome.sarRecall);

    return (
        <Box className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SectionCard
                title="What you save"
                subtitle="Work your team would no longer do."
                icon={<SavingsOutlinedIcon fontSize="small" />}
                accent="#38BDF8"
            >
                <Box className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Stat
                        label="Alerts not reviewed"
                        value={percent(1 - reviewFraction, 0)}
                        detail={`${count(alertsAvoided)} of ${count(simulator.totalAlertCount)} alerts`}
                        accent="#38BDF8"
                        definition={DEFINITIONS.workloadSaved}
                        indicative={indicative}
                    />
                    {/*
                      A staffing figure extrapolated from a handful of cases is a
                      claim the sample cannot support, so none is made.
                    */}
                    <Stat
                        label="Investigator time freed"
                        value={indicative ? 'Not estimated' : fte === null ? '—' : `${fte.toFixed(1)} FTE`}
                        // The assumption is named next to the number rather than
                        // buried in a constant, and the slider panel below lets
                        // the reader change it.
                        detail={indicative
                            ? 'Too few cases to estimate staffing'
                            : hours === null
                                ? 'Set an average handling time below'
                                : `about ${count(Math.round(hours))} hours, at ${minutesPerAlert} min per alert`}
                        accent="#38BDF8"
                        indicative={indicative}
                    />
                </Box>
            </SectionCard>

            <SectionCard
                title="What you keep"
                subtitle="Risk coverage at that same level."
                icon={<ShieldOutlinedIcon fontSize="small" />}
                accent="#10B981"
            >
                {!hasValue(outcome.truePositiveRecall) && !hasValue(outcome.sarRecall) ? (
                    <NotComputed detail="This backtest recorded no coverage curve, so what you keep at this level cannot be stated." />
                ) : (
                    <Box className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <Stat
                            label="Confirmed cases reached"
                            value={percent(outcome.truePositiveRecall, 0)}
                            detail={confirmedMissed === null
                                ? undefined
                                : `${count(confirmedMissed)} of ${count(summary.totals.truePositives)} not reviewed`}
                            accent="#10B981"
                            definition={DEFINITIONS.riskRetained}
                            indicative={indicative}
                        />
                        <Stat
                            label="SARs reached"
                            value={percent(outcome.sarRecall, 0)}
                            detail={sarsMissed === null
                                ? undefined
                                : `${count(sarsMissed)} of ${count(summary.totals.sarsFiled)} not reviewed`}
                            accent="#10B981"
                            definition={DEFINITIONS.sarConcentration}
                            indicative={indicative}
                        />
                    </Box>
                )}
            </SectionCard>
        </Box>
    );
};

interface QueueQualityProps {
    summary: ExecutiveSummaryResponse | null;
    indicative?: boolean;
    loading?: boolean;
    error?: string | null;
    onRetry?: () => void;
}

/**
 * The third benefit, which is neither a saving nor a coverage figure: the alerts
 * an investigator opens first are likelier to be real.
 *
 * Independent of where the review level is set, so it sits apart from the two
 * columns above rather than inside them.
 */
export const QueueQuality: React.FC<QueueQualityProps> = ({ summary, indicative = false, loading, error, onRetry }) => {
    if (error) {
        return (
            <SectionCard title="What improves for investigators" icon={<TrendingUpIcon fontSize="small" />}>
                <PanelError message={error} onRetry={onRetry} />
            </SectionCard>
        );
    }
    if (loading) return <PanelSkeleton height={200} />;
    if (!summary) return null;

    const q = summary.kpis.urgentQueueHitRate;

    return (
        <SectionCard
            title="What improves for investigators"
            subtitle="How often an alert at the top of the queue turns out to be genuine."
            icon={<TrendingUpIcon fontSize="small" />}
            accent="#FFDE59"
        >
            <Box className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <Stat
                    label="With the agent"
                    value={percent(q.hitRate, 0)}
                    detail={`${count(q.confirmedInTier)} confirmed in ${count(q.alertsInTier)} urgent alerts`}
                    accent="#10B981"
                    definition={DEFINITIONS.urgentHitRate}
                    indicative={indicative}
                />
                <Stat
                    label="With your current rules"
                    value={percent(q.incumbentHitRate, 0)}
                    detail={`across ${count(q.incumbentAlertsInTier)} alerts they mark urgent`}
                    accent="#94A3B8"
                    indicative={indicative}
                />
                <Stat
                    label="Working alerts unranked"
                    value={percent(q.unrankedHitRate, 0)}
                    detail="if alerts were opened in no particular order"
                    accent="#64748B"
                    indicative={indicative}
                />
            </Box>

            {/* The lift is a headline claim; a small sample cannot carry one. */}
            {hasValue(q.lift) && !indicative && (
                <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft'), mt: 2.5, pt: 2, borderTop: `1px solid ${dsVar('line')}`, lineHeight: 1.6 }}>
                    An investigator opening an urgent alert finds a genuine case{' '}
                    <Box component="span" sx={{ color: dsVar('accent-fg'), fontWeight: 700 }}>
                        {(q.lift as number).toFixed(1)}× as often
                    </Box>
                    {' '}as they do under your current rules — less time spent clearing false alarms before reaching real risk.
                </Typography>
            )}
        </SectionCard>
    );
};
